import React, { createContext, useState, useEffect } from 'react';

export const AppContext = createContext();

export const AppProvider = ({ children }) => {
  // API Error State
  const [apiError, setAPIError] = useState(null);

  // Actor Model States (for content generation)
  const [Actor_Model_apiProvider, setActor_Model_apiProvider] = useState(
    localStorage.getItem('Actor-apiProvider') || 'Google Gemini'
  );
  const [Actor_Model_apiModel, setActor_Model_apiModel] = useState(
    localStorage.getItem('Actor-apiModel') || 'gemini-1.5-pro'
  );
  const [temperature_val_Actor_Model_apiProvider, setTemperature_val_Actor_Model_apiProvider] = useState(
    parseFloat(localStorage.getItem('Actor-temperature_val')) || 0.3
  );

  // Critic Model States (for evaluation)
  const [Critic_Model_apiProvider, setCritic_Model_apiProvider] = useState(
    localStorage.getItem('Critic-apiProvider') || 'Google Gemini'
  );
  const [Critic_Model_apiModel, setCritic_Model_apiModel] = useState(
    localStorage.getItem('Critic-apiModel') || 'gemini-1.5-pro'
  );
  const [temperature_val_Critic_Model_apiProvider, setTemperature_val_Critic_Model_apiProvider] = useState(
    parseFloat(localStorage.getItem('Critic-temperature_val')) || 0.3
  );

  // AI Agent Model States (for initial research)
  const [AI_Agent_Model_apiProvider, setAI_Agent_Model_apiProvider] = useState(
    localStorage.getItem('AIAgent-apiProvider') || 'Google Gemini'
  );
  const [AI_Agent_Model_apiModel, setAI_Agent_Model_apiModel] = useState(
    localStorage.getItem('AIAgent-apiModel') || 'gemini-1.5-pro'
  );

  // Application States
  const [isLoading, setIsLoading] = useState(false);
  const [thinkingMessage, setThinkingMessage] = useState('');
  const [currentIteration, setCurrentIteration] = useState(0);
  const [maxIterations, setMaxIterations] = useState(3);
  const [researchTopic, setResearchTopic] = useState('');
  const [analysisResults, setAnalysisResults] = useState(null);
  const [chatHistory, setChatHistory] = useState([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  
  // User Authentication State
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [requestsRemaining, setRequestsRemaining] = useState(3);

  // Profile Count Change State
  const [profileCountChange, setProfileCountChange] = useState(5); // Default to 5 profiles

  // Persist Actor Model changes to localStorage
  useEffect(() => {
    localStorage.setItem('Actor-apiProvider', Actor_Model_apiProvider);
  }, [Actor_Model_apiProvider]);

  useEffect(() => {
    localStorage.setItem('Actor-apiModel', Actor_Model_apiModel);
  }, [Actor_Model_apiModel]);

  useEffect(() => {
    localStorage.setItem('Actor-temperature_val', temperature_val_Actor_Model_apiProvider.toString());
  }, [temperature_val_Actor_Model_apiProvider]);

  // Persist Critic Model changes to localStorage
  useEffect(() => {
    localStorage.setItem('Critic-apiProvider', Critic_Model_apiProvider);
  }, [Critic_Model_apiProvider]);

  useEffect(() => {
    localStorage.setItem('Critic-apiModel', Critic_Model_apiModel);
  }, [Critic_Model_apiModel]);

  useEffect(() => {
    localStorage.setItem('Critic-temperature_val', temperature_val_Critic_Model_apiProvider.toString());
  }, [temperature_val_Critic_Model_apiProvider]);

  // Persist AI Agent Model changes to localStorage
  useEffect(() => {
    if (AI_Agent_Model_apiProvider) {
      localStorage.setItem('AIAgent-apiProvider', AI_Agent_Model_apiProvider);
    }
  }, [AI_Agent_Model_apiProvider]);

  useEffect(() => {
    if (AI_Agent_Model_apiModel) {
      localStorage.setItem('AIAgent-apiModel', AI_Agent_Model_apiModel);
    }
  }, [AI_Agent_Model_apiModel]);

  // Helper function to get model configuration
  const getModelConfig = (role = 'actor') => {
    if (role === 'actor') {
      return {
        provider: Actor_Model_apiProvider,
        model: Actor_Model_apiModel,
        temperature: temperature_val_Actor_Model_apiProvider
      };
    } else if (role === 'critic') {
      return {
        provider: Critic_Model_apiProvider,
        model: Critic_Model_apiModel,
        temperature: temperature_val_Critic_Model_apiProvider
      };
    } else if (role === 'agent') {
      return {
        provider: AI_Agent_Model_apiProvider,
        model: AI_Agent_Model_apiModel,
        temperature: 0.3 // Default temperature for AI Agent
      };
    }
  };

  // Clear error after 10 seconds
  useEffect(() => {
    if (apiError) {
      const timer = setTimeout(() => {
        setAPIError(null);
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [apiError]);

  const contextValue = {
    // API Error Management
    apiError,
    setAPIError,

    // Actor Model Configuration
    Actor_Model_apiProvider,
    setActor_Model_apiProvider,
    Actor_Model_apiModel,
    setActor_Model_apiModel,
    temperature_val_Actor_Model_apiProvider,
    setTemperature_val_Actor_Model_apiProvider,

    // Critic Model Configuration
    Critic_Model_apiProvider,
    setCritic_Model_apiProvider,
    Critic_Model_apiModel,
    setCritic_Model_apiModel,
    temperature_val_Critic_Model_apiProvider,
    setTemperature_val_Critic_Model_apiProvider,

    // AI Agent Model Configuration
    AI_Agent_Model_apiProvider,
    setAI_Agent_Model_apiProvider,
    AI_Agent_Model_apiModel,
    setAI_Agent_Model_apiModel,

    // Application States
    isLoading,
    setIsLoading,
    thinkingMessage,
    setThinkingMessage,
    currentIteration,
    setCurrentIteration,
    maxIterations,
    setMaxIterations,
    researchTopic,
    setResearchTopic,
    analysisResults,
    setAnalysisResults,
    chatHistory,
    setChatHistory,
    isAnalyzing,
    setIsAnalyzing,

    // User Authentication
    user,
    setUser,
    isAuthenticated,
    setIsAuthenticated,
    subscription,
    setSubscription,
    requestsRemaining,
    setRequestsRemaining,

    // Profile Count
    profileCountChange,
    setProfileCountChange,

    // Helper Functions
    getModelConfig,
  };

  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
};

export default AppContext;